import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { venuesAPI, eventsAPI } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuthStore } from '../../../../store/authStore';
import {
  MapPin,
  Phone,
  Mail,
  Users,
  Search,
  CheckCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  Eye,
  Map
} from 'lucide-react';
import './VenueManagementPanel.css';

const VenueManagementPanel = () => {
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [capacityFilter, setCapacityFilter] = useState('');

  const roleFlags = {
    isAdmin: user?.isAdmin || user?.admin,
    isPlanner: user?.isPlanner || user?.planner
  };

  const loadVenues = useCallback(async () => {
    try {
      setLoading(true);
      const [venueResponse, eventsResponse] = await Promise.all([
        venuesAPI.getAll(),
        eventsAPI.getAll()
      ]);

      const venueList = Array.isArray(venueResponse) ? venueResponse : venueResponse?.content || [];
      const eventsList = Array.isArray(eventsResponse) ? eventsResponse : eventsResponse?.content || [];

      const eventsByVenue = eventsList.reduce((acc, evt) => {
        const venueId = evt.venueId || evt.venue?.id;
        if (venueId) {
          acc[venueId] = (acc[venueId] || 0) + 1;
        }
        return acc;
      }, {});

      const augmentedVenues = venueList.map((venue) => ({
        ...venue,
        eventsCount: eventsByVenue[venue.id] || 0
      }));

      setVenues(augmentedVenues);
    } catch (error) {
      console.error('Error loading venues:', error);
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const filterVenues = useCallback(() => {
    let results = [...venues];

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      results = results.filter(venue =>
        venue.name?.toLowerCase().includes(query) ||
        venue.address?.city?.toLowerCase().includes(query) ||
        venue.address?.streetAddress?.toLowerCase().includes(query) ||
        venue.address?.formattedAddress?.toLowerCase().includes(query)
      );
    }

    if (cityFilter !== 'all') {
      results = results.filter(venue => venue.address?.city === cityFilter);
    }

    if (statusFilter !== 'all') {
      results = results.filter(venue =>
        statusFilter === 'active' ? venue.isActive !== false : venue.isActive === false
      );
    }

    if (capacityFilter) {
      const minCapacity = Number(capacityFilter) || 0;
      results = results.filter(venue => (venue.capacity || 0) >= minCapacity);
    }

    setFilteredVenues(results);
  }, [venues, searchTerm, cityFilter, statusFilter, capacityFilter]);

  useEffect(() => {
    filterVenues();
  }, [filterVenues]);

  const cityOptions = useMemo(() => {
    const cities = new Set();
    venues.forEach(venue => {
      if (venue.address?.city) {
        cities.add(venue.address.city);
      }
    });
    return Array.from(cities).sort();
  }, [venues]);

  const venueStats = useMemo(() => {
    const totalVenues = venues.length;
    const activeVenues = venues.filter(venue => venue.isActive !== false).length;
    const inactiveVenues = totalVenues - activeVenues;
    const totalCapacity = venues.reduce((sum, venue) => sum + (venue.capacity || 0), 0);

    return {
      totalVenues,
      activeVenues,
      inactiveVenues,
      totalCapacity
    };
  }, [venues]);

  const getVenueBasePath = () => (roleFlags.isAdmin ? '/admin/venues' : '/planner/venues');

  const handleCreateVenue = () => {
    navigate(`${getVenueBasePath()}/create`);
  };

  const handleEditVenue = (venue) => {
    if (!roleFlags.isAdmin && !roleFlags.isPlanner) {
      showAlert({
        title: 'Access denied',
        message: 'You do not have permission to edit venues.'
      });
      return;
    }
    navigate(`${getVenueBasePath()}/${venue.id}`);
  };

  const handleDeleteVenue = async (venueId) => {
    showAlert({
      title: 'Delete venue?',
      message: 'Are you sure you want to delete this venue? This action cannot be undone.',
      primaryLabel: 'Delete',
      secondaryLabel: 'Cancel',
      onPrimary: async () => {
        try {
          await venuesAPI.delete(venueId);
          setVenues((prev) => prev.filter((venue) => venue.id !== venueId));
        } catch (error) {
          console.error('Error deleting venue:', error);
          showAlert({
            title: 'Delete failed',
            message: 'Failed to delete venue. Please try again.'
          });
        }
      }
    });
  };

  return (
    <div className="venues-panel-shell">
      <div className="venues-panel-header">
        <div className="venues-header-title">
          <h1><Map size={24} className="text-blue-500" /> Venue Control Center</h1>
          <p>Manage seating layouts, locations, and capacity.</p>
        </div>

        <div className="venues-header-actions">
          <button
            className="venues-btn secondary"
            onClick={loadVenues}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            className="venues-btn primary"
            onClick={handleCreateVenue}
          >
            <Plus size={16} /> New Venue
          </button>
        </div>
      </div>

      <div className="venues-stats-row">
        <div className="venues-stat-card" style={{ '--accent-color': '#3b82f6' }}>
          <div className="stat-header">
            <div className="stat-icon-box"><Map size={18} /></div>
            <span className="stat-label">Total Venues</span>
          </div>
          <div className="stat-value">{venueStats.totalVenues}</div>
          <div className="stat-subtext">Across all regions</div>
        </div>

        <div className="venues-stat-card" style={{ '--accent-color': '#10b981' }}>
          <div className="stat-header">
            <div className="stat-icon-box"><CheckCircle size={18} /></div>
            <span className="stat-label">Active</span>
          </div>
          <div className="stat-value">{venueStats.activeVenues}</div>
          <div className="stat-subtext">Ready for booking</div>
        </div>

        <div className="venues-stat-card" style={{ '--accent-color': '#f59e0b' }}>
          <div className="stat-header">
            <div className="stat-icon-box"><AlertTriangle size={18} /></div>
            <span className="stat-label">Inactive</span>
          </div>
          <div className="stat-value">{venueStats.inactiveVenues}</div>
          <div className="stat-subtext">Maintenance / Closed</div>
        </div>

        <div className="venues-stat-card" style={{ '--accent-color': '#8b5cf6' }}>
          <div className="stat-header">
            <div className="stat-icon-box"><Users size={18} /></div>
            <span className="stat-label">Total Capacity</span>
          </div>
          <div className="stat-value">{venueStats.totalCapacity.toLocaleString()}</div>
          <div className="stat-subtext">Seats managed</div>
        </div>
      </div>

      <div className="venues-filter-bar">
        <div className="venues-search-wrapper">
          <Search size={16} className="venues-search-icon" />
          <input
            type="text"
            className="venues-input search"
            placeholder="Search venue names or addresses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="venues-filter-group">
          <label>City</label>
          <select
            className="venues-select"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="all">All Cities</option>
            {cityOptions.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        <div className="venues-filter-group">
          <label>Status</label>
          <select
            className="venues-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="venues-filter-group" style={{ minWidth: '100px' }}>
          <label>Min Capacity</label>
          <input
            type="number"
            className="venues-input"
            min="0"
            placeholder="0"
            value={capacityFilter}
            onChange={(e) => setCapacityFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="venues-empty-state">
          <RefreshCw size={48} className="animate-spin" style={{ opacity: 0.5 }} />
          <h3>Loading venues...</h3>
        </div>
      ) : filteredVenues.length === 0 ? (
        <div className="venues-empty-state">
          <Map size={48} style={{ opacity: 0.3 }} />
          <h3>No venues found</h3>
          <p>Try adjusting filters or searching for a different name.</p>
          {venues.length === 0 && (
            <button className="venues-btn primary" onClick={handleCreateVenue} style={{ marginTop: '1rem' }}>
              Create First Venue
            </button>
          )}
        </div>
      ) : (
        <div className="venues-grid">
          {filteredVenues.map((venue) => {
            const isActive = venue.isActive !== false;
            return (
              <div className="venue-card" key={venue.id}>
                <div className="venue-image-container">
                  {venue.imageUrl ? (
                    <div className="venue-image-bg" style={{ backgroundImage: `url(${venue.imageUrl})` }} />
                  ) : (
                    <div className="venue-placeholder"><Map size={48} /></div>
                  )}
                  <div className={`venue-status-badge ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
                    {isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>

                <div className="venue-content">
                  <div>
                    <h3 className="venue-title">{venue.name || 'Untitled Venue'}</h3>
                  </div>

                  <div className="venue-info-list">
                    <div className="venue-info-item">
                      <MapPin size={14} />
                      <span>
                        {venue.address?.city || 'Unknown City'}
                        {venue.address?.stateProvince && `, ${venue.address.stateProvince}`}
                      </span>
                    </div>
                    {(venue.phone || venue.email) && (
                      <div className="venue-info-item">
                        {venue.email ? <Mail size={14} /> : <Phone size={14} />}
                        <span>{venue.email || venue.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="venue-stats-grid">
                    <div className="venue-stat-mini">
                      <span className="mini-value">{(venue.capacity || 0).toLocaleString()}</span>
                      <span className="mini-label">Capacity</span>
                    </div>
                    <div className="venue-stat-mini">
                      <span className="mini-value">{venue.eventsCount || 0}</span>
                      <span className="mini-label">Events</span>
                    </div>
                  </div>

                  <div className="venue-actions">
                    <button className="venue-action-btn" onClick={() => handleEditVenue(venue)}>
                      <Edit2 size={14} /> Edit
                    </button>
                    <button className="venue-action-btn" onClick={() => navigate(`${getVenueBasePath()}/${venue.id}`)}>
                      <Eye size={14} /> View
                    </button>
                    <button className="venue-action-btn danger" onClick={() => handleDeleteVenue(venue.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VenueManagementPanel;
